import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetLateGoerRatioQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0F172A] text-white p-2 rounded border border-[#1f2d40] text-xs">
        <p className="text-center font-bold mb-1">
          {dayjs(label).format('MMM D, YYYY')}
        </p>
        <p className="text-white">Ratio: {payload[0].value?.toFixed(2)}%</p>
      </div>
    );
  }

  return null;
};

const LateGoerRatio = ({
  department_id,
  office_id,
  filter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
}) => {
  const earlyGoerFilter = filter.workForceCulture;

  const { data: ratioData, error } = useGetLateGoerRatioQuery({
    start_date: earlyGoerFilter.start_date || '',
    end_date: earlyGoerFilter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch early goer ratio stats');

  const chartData = (ratioData?.data?.data?.daily_breakdown || []).map(
    (item) => ({
      name: item.date,
      value: item.late_goer_ratio_percentage,
    }),
  );

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full">
      <Flex
        justify="space-between"
        align="center"
        className="mb-6 text-white p-2"
      >
        <h4 className="text-xl text-white">Late Goer Trend</h4>
        <span className="text-theme-primary text-sm">
          {formatDateRange(
            earlyGoerFilter.start_date,
            earlyGoerFilter.end_date,
            { separator: '-' },
          )}
        </span>
      </Flex>

      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorGoer" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ffffff" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#ffffff" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#1e2a38"
            vertical={false}
          />
          <XAxis
            dataKey="name"
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(val) => dayjs(val).format('MMM D')}
          />
          <YAxis
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => `${value}%`}
            domain={[0, 100]}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: '#ffffff', strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#ffffff"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorGoer)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LateGoerRatio;
